import type { Step, Tone } from "../api/types";

/** Colour class for a step, as in the demo's lvClass. */
export function lvClass(l: number): "l0" | "lm1" | "lm2" | "l1" | "l2" {
  return l === 0 ? "l0" : l === -1 ? "lm1" : l === -2 ? "lm2" : l === 1 ? "l1" : "l2";
}

/** A spot that differs in a way that needs no fixing (a little more room, a cropped hem) reads calm, the
 *  way a little extra room does, not as a warning. */
export function spotClass(step: number, neutral: boolean | undefined): ReturnType<typeof lvClass> {
  return neutral && step !== 0 ? "l1" : lvClass(step);
}

export function clampStep(n: number): Step {
  return Math.max(-2, Math.min(2, Math.round(n))) as Step;
}

const STEP_WORD: Record<string, Record<string, string>> = {
  _: { "-2": "too tight", "-1": "snug", "0": "like your favorite", "1": "roomier", "2": "loose" },
  length: { "-2": "cropped", "-1": "shorter", "0": "the same length", "1": "longer", "2": "too long" },
  neckline: { "-2": "sitting high", "-1": "sitting flat", "0": "like your favorite", "1": "gaping a little", "2": "gaping" },
};

/** Words for a step, used for "between X and Y" when the server gives a range. */
export function stepWord(zone: string, step: number): string {
  const k = /length$/.test(zone) ? "length" : zone === "neckline" ? "neckline" : "_";
  return STEP_WORD[k][String(clampStep(step))] ?? STEP_WORD._[String(clampStep(step))];
}

export function rangeText(zone: string, range: [number, number] | null): string | null {
  if (!range) return null;
  const [a, b] = range[0] <= range[1] ? range : [range[1], range[0]];
  if (a === b) return null;
  return `Somewhere between ${stepWord(zone, a)} and ${stepWord(zone, b)}.`;
}

export const SHORT_READ: Record<Tone, string> = { good: "Close", ok: "Small fix", mixed: "Trade-offs", bad: "Won't fit" };

export function shortTitle(t: string | null | undefined): string {
  return String(t || "")
    .split(" ")
    .filter((w) => !/^(womens?|mens?|the)$/i.test(w))
    .slice(0, 4)
    .join(" ");
}

export function sortedSizes(sizes: { label: string; sort_order: number }[]): string[] {
  return sizes.slice().sort((a, b) => a.sort_order - b.sort_order).map((s) => s.label);
}

export function looksLikeLink(raw: string): string | null {
  let s = raw.trim();
  if (!s) return null;
  if (!/^https?:\/\//i.test(s)) s = "https://" + s;
  try {
    const u = new URL(s);
    if (!/\./.test(u.hostname)) return null;
    return u.href;
  } catch {
    return null;
  }
}

/** How sure a note is, in words: the share of the bell curve inside the step we show. Below
 *  "high" it reads "medium" for now: every read starts from size charts until fit reports come in. */
export function confLevel(c: number): "Medium" | "High" {
  return c >= 70 ? "High" : "Medium";
}
